import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Store, Image as ImageIcon } from 'lucide-react';
import type { TakeAwayStoreToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useTakeawayStoresList, useTakeawayStoreMutations } from '~/hooks/use-takeaway-stores';
import { useCountriesList } from '~/hooks/use-countries';
import { useCountryRegionsList } from '~/hooks/use-country-regions';

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

/** Pickup points (Σημεία Παραλαβής) list — parity with the legacy admin grid; standalone editor on add/edit. */
export function TakeawayStoresGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data, isLoading, isError, refetch } = useTakeawayStoresList();
	const { data: countries } = useCountriesList();
	const { data: regions } = useCountryRegionsList();
	const { remove } = useTakeawayStoreMutations();

	const [deleteRow, setDeleteRow] = useState<TakeAwayStoreToken | null>(null);

	const countryName = useMemo(() => {
		const map = new Map<number, string>();
		for (const c of countries ?? []) if (c.id != null) map.set(c.id, c.nm ?? '');
		return (id?: number | null) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [countries]);

	const regionName = useMemo(() => {
		const map = new Map<number, string>();
		for (const r of regions ?? []) if (r.id != null) map.set(r.id, r.nm ?? '');
		return (id?: number | null) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [regions]);

	const columns = useMemo<SystemGridColumn<TakeAwayStoreToken>[]>(
		() => [
			{
				key: 'image',
				header: t('orders.takeawayStores.columns.image'),
				width: '70px',
				toggleable: false,
				render: r =>
					r.img1 ? (
						<img src={r.img1} alt={r.nm ?? ''} className="h-9 w-9 rounded border border-border object-contain" />
					) : (
						<div className="flex h-9 w-9 items-center justify-center rounded border border-border bg-elevated">
							<ImageIcon size={14} className="text-foreground-disabled" />
						</div>
					)
			},
			{
				key: 'name',
				header: t('orders.takeawayStores.columns.name'),
				width: 'minmax(0,1.4fr)',
				sortValue: r => r.nm ?? '',
				exportValue: r => r.nm ?? '',
				render: r => (
					<span className="truncate text-sm font-medium text-foreground" title={r.nm ?? ''}>
						{r.nm || '—'}
					</span>
				)
			},
			{
				key: 'country',
				header: t('orders.takeawayStores.columns.country'),
				width: '130px',
				sortValue: r => countryName(r.cid),
				exportValue: r => countryName(r.cid),
				render: r => <span className="truncate text-sm text-foreground-muted">{countryName(r.cid)}</span>
			},
			{
				key: 'region',
				header: t('orders.takeawayStores.columns.region'),
				width: '130px',
				defaultVisible: false,
				sortValue: r => regionName(r.rid),
				exportValue: r => regionName(r.rid),
				render: r => <span className="truncate text-sm text-foreground-muted">{regionName(r.rid)}</span>
			},
			{
				key: 'address',
				header: t('orders.takeawayStores.columns.address'),
				width: 'minmax(0,1fr)',
				sortValue: r => r.adl ?? '',
				exportValue: r => r.adl ?? '',
				render: r => (
					<span className="truncate text-sm text-foreground-muted" title={r.adl ?? ''}>
						{r.adl || '—'}
					</span>
				)
			},
			{
				key: 'email',
				header: t('orders.takeawayStores.columns.email'),
				width: '200px',
				defaultVisible: false,
				sortValue: r => r.eml ?? '',
				exportValue: r => r.eml ?? '',
				render: r => <span className="truncate text-sm text-foreground-muted">{r.eml || '—'}</span>
			},
			{
				key: 'visible',
				header: t('orders.takeawayStores.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: r => (r.iv ? 1 : 0),
				exportValue: r => (r.iv ? t('common.yes') : t('common.no')),
				render: r => <Bool on={Boolean(r.iv)} />
			},
			{
				key: 'sort',
				header: t('orders.takeawayStores.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: r => r.si ?? 0,
				exportValue: r => r.si ?? 0,
				render: r => <span className="text-sm tabular-nums text-foreground-muted">{r.si ?? 0}</span>
			}
		],
		[t, countryName, regionName]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<TakeAwayStoreToken>
				gridId="takeaway-stores"
				title={t('orders.takeawayStores.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={r => r.id ?? r.nm}
				columns={columns}
				searchPredicate={(r, q) => `${r.nm ?? ''} ${r.adl ?? ''} ${r.ct ?? ''} ${r.eml ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Store size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('orders.takeawayStores.add')}
				onAdd={() => navigate('/orders/pickup-points/create')}
				onEdit={r => navigate(`/orders/pickup-points/${r.id}`)}
				onDelete={setDeleteRow}
				exportFilename="takeaway-stores"
			/>

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('orders.takeawayStores.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('orders.takeawayStores.deleteBody', { name: deleteRow?.nm ?? '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
